import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useReturnPath } from "hooks/useReturnPath"
import { useFormik } from "formik"
import * as Yup from "yup"
import Select from "react-select"
import { toast } from "react-toastify"
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap"
import PageWrapper from "components/Common/PageWrapper"
import ComplimentaryItemsManager from "components/Common/ComplimentaryItemsManager"
import {
  useMoneyReceipt,
  useCreateMoneyReceipt,
  useEditMoneyReceipt,
} from "hooks/useMoneyReceipt"
import { useAllMoneyReceiptComplimentaryItems } from "hooks/useComplimentaryItem"
import { useAuth } from "hooks/authHook"
import { ACCESS } from "constants/accessPermissions"
import { useAllQuotations } from "hooks/useQuotation"
import { useCustomer, useCustomers, useCustomerSearchAPI } from "hooks/useCustomer"
import CustomerSearch from "pages/QuotationPages/Quotation/Customer/CustomerSearch"
import {
  useVehicleStocks,
  useVehicleSearchQuery,
  useVehicleStock,
} from "hooks/stockHook"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { useToastMessage } from "hooks/useToastMessage"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import {
  buildCreatableOptions,
  buildCustomerTypeOptions,
  buildCustomerSuggestionBuckets,
  formatCustomerTypeLabel,
  getCustomerDepartmentText,
  getCustomerOrganizationText,
  getCustomerTypeFieldMeta,
  resolveCustomerTypeKey,
} from "helpers/customerOnboarding"
import { appendQueryParam } from "utils/urlParams"
import "./MoneyReceiptAddEdit.css"

const extractCollection = value => {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.data)) return value.data
  return []
}

const getQuotationPricing = quotation => {
  const priceItems = quotation?.pdf_content?.price_items ?? []
  const carPrice =
    Number(
      priceItems.find(item =>
        /unit price|car price/i.test(item?.description || "")
      )?.amount
    ) ||
    Number(quotation?.pdf_content?.total_amount) ||
    0
  const registrationCost =
    Number(
      priceItems.find(item => /registration/i.test(item?.description || ""))
        ?.amount
    ) || 0

  return { carPrice, registrationCost }
}

const formatAmount = value =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })

const buildAdditionalCostItem = item => ({
  name: item?.name || "",
  amount: item?.amount ?? "",
})

const createEmptyAdditionalCost = () => ({ name: "", amount: "" })

const getAdditionalCostsTotal = additionalCosts =>
  (additionalCosts || []).reduce(
    (total, item) => total + Number(item?.amount || 0),
    0
  )

const normalizeAdditionalCosts = additionalCosts =>
  (additionalCosts || []).map(buildAdditionalCostItem)

const normalizeCustomerTypeValue = value => resolveCustomerTypeKey(value)

const getCustomerTypeKey = customer =>
  resolveCustomerTypeKey(customer?.customer_type)

const getCustomerOrganizationLabel = customerType => {
  if (customerType === "banker") return "Bank"
  if (customerType === "service") return "Company"
  return "Organization"
}

const buildCustomerPdfContent = customer => ({
  name: customer?.name || "",
  mobile: customer?.mobile || "",
  email: customer?.email || "",
  customer_type: getCustomerTypeKey(customer),
  designation: getDisplayText(customer?.designation),
  company_name:
    getCustomerTypeKey(customer) === "service"
      ? customer?.company_name || ""
      : "",
  bank_name:
    getCustomerTypeKey(customer) === "banker" ? customer?.bank_name || "" : "",
  branch_name:
    getCustomerTypeKey(customer) === "banker"
      ? customer?.branch_name || ""
      : "",
  address: customer?.full_address || "",
})

const buildVehicleName = vehicle =>
  [
    getDisplayText(vehicle?.brand),
    getDisplayText(vehicle?.model),
    getDisplayText(vehicle?.package),
    getDisplayText(vehicle?.engine_type),
  ]
    .filter(Boolean)
    .join(" ")

const buildVehiclePdfContent = (
  vehicle,
  quotation,
  existingVehicleInfo = {},
  pricing = {}
) => ({
  stock_id: vehicle?.stock_id || existingVehicleInfo?.stock_id || "",
  full_chassis:
    vehicle?.full_chassis || existingVehicleInfo?.full_chassis || "",
  full_engine: vehicle?.full_engine || existingVehicleInfo?.full_engine || "",
  quotation: quotation?.qid || existingVehicleInfo?.quotation || "",
  name: buildVehicleName(vehicle) || existingVehicleInfo?.name || "",
  chassis_no:
    vehicle?.full_chassis ||
    vehicle?.chassis_number ||
    existingVehicleInfo?.chassis_no ||
    "",
  brta_reg: vehicle?.brta_reg_no || existingVehicleInfo?.brta_reg || "",
  year_model:
    getDisplayText(vehicle?.year) || existingVehicleInfo?.year_model || "",
  color:
    getDisplayText(vehicle?.exterior_color) || existingVehicleInfo?.color || "",
  cc: getDisplayText(vehicle?.engine_c_c) || existingVehicleInfo?.cc || "",
  fuel: getDisplayText(vehicle?.fuel_type) || existingVehicleInfo?.fuel || "",
  descriptions:
    vehicle?.short_descriptions ||
    vehicle?.full_descriptions ||
    existingVehicleInfo?.descriptions ||
    "",
  price: pricing?.carPrice ?? existingVehicleInfo?.price ?? "",
  registration_cost:
    pricing?.registrationCost ?? existingVehicleInfo?.registration_cost ?? "",
  discount: existingVehicleInfo?.discount ?? vehicle?.discount_price ?? "",
})

const getDisplayText = value => {
  if (!value) return ""
  if (typeof value === "string") return value
  if (typeof value === "object")
    return value?.name || value?.title || value?.label || ""
  return String(value)
}

const normalizeMatchValue = value =>
  String(value || "")
    .trim()
    .toLowerCase()

const hasCustomerIdentifier = values => {
  const customerInfo = values?.pdf_content?.customer_info || {}
  return Boolean(normalizeMatchValue(customerInfo?.name))
}

const hasVehicleIdentifier = values => {
  const vehicleInfo = values?.pdf_content?.vehicle_info || {}
  return Boolean(
    normalizeMatchValue(vehicleInfo?.full_chassis) ||
      normalizeMatchValue(vehicleInfo?.full_engine)
  )
}

const findMatchingVehicle = (values, vehicles) => {
  const vehicleInfo = values?.pdf_content?.vehicle_info || {}
  const fullChassis = normalizeMatchValue(vehicleInfo?.full_chassis)
  const fullEngine = normalizeMatchValue(vehicleInfo?.full_engine)

  if (!fullChassis && !fullEngine) return null

  return (
    vehicles.find(item => {
      const itemChassis = normalizeMatchValue(item?.full_chassis)
      const itemEngine = normalizeMatchValue(item?.full_engine)
      return (
        (fullChassis && itemChassis === fullChassis) ||
        (fullEngine && itemEngine === fullEngine)
      )
    }) || null
  )
}

const getVehiclePreviewLabel = vehicle =>
  [
    getDisplayText(vehicle?.brand),
    getDisplayText(vehicle?.model),
    getDisplayText(vehicle?.package),
    getDisplayText(vehicle?.engine_type),
  ]
    .filter(Boolean)
    .join(" ")

const buildCustomerPreviewRows = customer => {
  if (!customer) return []

  const customerType = getCustomerTypeKey(customer)
  const organizationLabel = getCustomerOrganizationLabel(customerType)
  const departmentLabel = customerType === "banker" ? "Branch" : "Department"

  return [
    { label: "Type", value: formatCustomerTypeLabel(customerType) || "-" },
    { label: "Mobile", value: customer?.mobile || "-" },
    { label: "Email", value: customer?.email || "-" },
    {
      label: "Designation",
      value: getDisplayText(customer?.designation) || "-",
    },
    {
      label: organizationLabel,
      value: getCustomerOrganizationText(customer) || "-",
    },
    {
      label: departmentLabel,
      value: getCustomerDepartmentText(customer) || "-",
    },
    { label: "Address", value: customer?.full_address || "-" },
  ]
}

const MoneyReceiptAddEditPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { fromPath } = useReturnPath("/money-receipts")
  const isEdit = Boolean(id)
  const fromQuotationId = searchParams.get("quotation_id")
  const prefillVehicleId = searchParams.get("vehicle_id")
  const prefillCuid = searchParams.get("cuid")
  // Vehicle came in prefilled from the stock view — keep it locked.
  const vehicleLocked = !!prefillVehicleId
  // Customer came in prefilled from the customer profile — keep it locked.
  const customerLocked = !!prefillCuid
  const [mode, setMode] = useState(fromQuotationId ? "quotation" : "manual")

  const { handleResponse, handleError } = useToastMessage()
  const { hasAnyPermission } = useAuth()

  const { data: moneyReceiptResponse, isLoading } = useMoneyReceipt(id)
  const { mutateAsync: createMoneyReceipt, isPending: creating } =
    useCreateMoneyReceipt()
  const { mutateAsync: editMoneyReceipt, isPending: editing } =
    useEditMoneyReceipt()
  const { data: quotationResponse } = useAllQuotations({
    per_page: 200,
  })
  const { data: customerResponse } = useCustomers({ all: true, per_page: 200 })
  const { data: prefillCustomerResponse, isLoading: prefillCustomerLoading } =
    useCustomer(prefillCuid, !isEdit && !!prefillCuid)
  const { data: vehicleResponse } = useVehicleStocks(
    { all: true, per_page: 200 },
    true
  )
  const { raw: filterOptionData, isLoading: customerLookupLoading } =
    useFilteredVehicleOptions()
  const { data: complimentaryItemsResponse } =
    useAllMoneyReceiptComplimentaryItems({}, true)
  const [selectedCustomerItem, setSelectedCustomerItem] = useState(null)
  const { customerSearchAPI, loading: customerSearching } =
    useCustomerSearchAPI({ limit: 8 })
  const [selectedVehicleItem, setSelectedVehicleItem] = useState(null)
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState("")
  const [showVehicleSuggestions, setShowVehicleSuggestions] = useState(false)
  const vehicleSearchRef = useRef(null)
  const { data: vehicleSearchResults, isLoading: vehicleSearching } =
    useVehicleSearchQuery(vehicleSearchTerm, { limit: 8 })
  const { data: prefillVehicleResponse, isLoading: prefillVehicleLoading } =
    useVehicleStock(!isEdit ? prefillVehicleId : null)
  const lastAutofilledQuotationIdRef = useRef(null)
  const lastEditPrefilledReceiptIdRef = useRef(null)
  const vehiclePrefilledRef = useRef(false)
  const cuidPrefilledRef = useRef(false)

  const moneyReceipt = moneyReceiptResponse?.data ?? {}
  const amountPaid = Number(moneyReceipt?.amount_paid || 0)
  const isPaidReceipt = isEdit && amountPaid > 0
  const quotations = extractCollection(quotationResponse?.data)
  const customers = extractCollection(customerResponse?.data)
  const vehicles = extractCollection(
    vehicleResponse?.data?.vehicles ?? vehicleResponse?.data
  )
  const customerLookupData = useMemo(() => {
    const suggestionBuckets = buildCustomerSuggestionBuckets(customers)
    const rawCustomerLookupData = filterOptionData?.customer ?? {}

    return {
      customer_types:
        rawCustomerLookupData?.customer_types ??
        suggestionBuckets.customerTypes ??
        [],
      designations:
        rawCustomerLookupData?.designations ??
        suggestionBuckets.designations ??
        [],
      companies:
        rawCustomerLookupData?.companies ?? suggestionBuckets.companies ?? [],
      banks: rawCustomerLookupData?.banks ?? suggestionBuckets.banks ?? [],
      branches:
        rawCustomerLookupData?.branches ?? suggestionBuckets.branches ?? [],
    }
  }, [customers, filterOptionData?.customer])

  const availableComplimentaryItems = complimentaryItemsResponse?.data || []

  const customerTypeSelectOptions = useMemo(
    () => buildCustomerTypeOptions(customerLookupData?.customer_types ?? []),
    [customerLookupData?.customer_types]
  )
  const designationSelectOptions = useMemo(
    () => buildCreatableOptions(customerLookupData?.designations ?? []),
    [customerLookupData?.designations]
  )
  const companySelectOptions = useMemo(
    () => buildCreatableOptions(customerLookupData?.companies ?? []),
    [customerLookupData?.companies]
  )
  const bankSelectOptions = useMemo(
    () => buildCreatableOptions(customerLookupData?.banks ?? []),
    [customerLookupData?.banks]
  )
  const branchSelectOptions = useMemo(
    () => buildCreatableOptions(customerLookupData?.branches ?? []),
    [customerLookupData?.branches]
  )

  const quotationOptions = useMemo(
    () =>
      quotations.map(item => ({
        value: item.id,
        label: `${item.qid} - ${item.customer?.name || "Unknown customer"}`,
        item,
      })),
    [quotations]
  )

  const initialValues = useMemo(() => {
    const fallbackQuotationId = Number(fromQuotationId || "") || ""
    const fallbackVehicleId = Number(prefillVehicleId || "") || ""

    return {
      quotation_id: moneyReceipt?.quotation_id ?? fallbackQuotationId,
      customer_id: moneyReceipt?.customer_id ?? "",
      vehicle_id: moneyReceipt?.vehicle_id ?? fallbackVehicleId,
      car_price: moneyReceipt?.car_price ?? "",
      registration_cost: moneyReceipt?.registration_cost ?? 0,
      issued_date:
        moneyReceipt?.issued_date?.slice?.(0, 10) ??
        new Date().toISOString().split("T")[0],
      notes: moneyReceipt?.notes ?? "",
      edit_reason: "",
      pdf_content: {
        customer_info: {
          name:
            moneyReceipt?.pdf_content?.customer_info?.name ??
            moneyReceipt?.customer?.name ??
            "",
          mobile:
            moneyReceipt?.pdf_content?.customer_info?.mobile ??
            moneyReceipt?.customer?.mobile ??
            "",
          email:
            moneyReceipt?.pdf_content?.customer_info?.email ??
            moneyReceipt?.customer?.email ??
            "",
          customer_type:
            normalizeCustomerTypeValue(
              moneyReceipt?.pdf_content?.customer_info?.customer_type ??
                moneyReceipt?.customer?.customer_type
            ) || "general",
          designation:
            moneyReceipt?.pdf_content?.customer_info?.designation ??
            moneyReceipt?.customer?.designation ??
            "",
          company_name:
            moneyReceipt?.pdf_content?.customer_info?.company_name ??
            (moneyReceipt?.customer?.customer_type === "service"
              ? moneyReceipt?.customer?.company_name
              : "") ??
            "",
          bank_name:
            moneyReceipt?.pdf_content?.customer_info?.bank_name ??
            (moneyReceipt?.customer?.customer_type === "banker"
              ? moneyReceipt?.customer?.bank_name
              : "") ??
            "",
          branch_name:
            moneyReceipt?.pdf_content?.customer_info?.branch_name ??
            (moneyReceipt?.customer?.customer_type === "banker"
              ? moneyReceipt?.customer?.branch_name
              : "") ??
            "",
          address:
            moneyReceipt?.pdf_content?.customer_info?.address ??
            moneyReceipt?.customer?.full_address ??
            "",
        },
        vehicle_info: {
          stock_id:
            moneyReceipt?.pdf_content?.vehicle_info?.stock_id ??
            moneyReceipt?.vehicle?.stock_id ??
            "",
          full_chassis:
            moneyReceipt?.pdf_content?.vehicle_info?.full_chassis ??
            moneyReceipt?.vehicle?.full_chassis ??
            "",
          full_engine:
            moneyReceipt?.pdf_content?.vehicle_info?.full_engine ??
            moneyReceipt?.vehicle?.full_engine ??
            "",
          quotation:
            moneyReceipt?.pdf_content?.vehicle_info?.quotation ??
            moneyReceipt?.quotation?.qid ??
            "",
          name:
            moneyReceipt?.pdf_content?.vehicle_info?.name ??
            buildVehicleName(moneyReceipt?.vehicle) ??
            "",
          chassis_no:
            moneyReceipt?.pdf_content?.vehicle_info?.chassis_no ??
            moneyReceipt?.vehicle?.full_chassis ??
            moneyReceipt?.vehicle?.chassis_number ??
            "",
          brta_reg:
            moneyReceipt?.pdf_content?.vehicle_info?.brta_reg ??
            moneyReceipt?.vehicle?.brta_reg_no ??
            "",
          year_model:
            moneyReceipt?.pdf_content?.vehicle_info?.year_model ??
            getDisplayText(moneyReceipt?.vehicle?.year) ??
            "",
          color:
            moneyReceipt?.pdf_content?.vehicle_info?.color ??
            getDisplayText(moneyReceipt?.vehicle?.exterior_color) ??
            "",
          cc:
            moneyReceipt?.pdf_content?.vehicle_info?.cc ??
            getDisplayText(moneyReceipt?.vehicle?.engine_c_c) ??
            "",
          fuel:
            moneyReceipt?.pdf_content?.vehicle_info?.fuel ??
            getDisplayText(moneyReceipt?.vehicle?.fuel_type) ??
            "",
          descriptions:
            moneyReceipt?.pdf_content?.vehicle_info?.descriptions ??
            moneyReceipt?.vehicle?.short_descriptions ??
            moneyReceipt?.vehicle?.full_descriptions ??
            "",
          price:
            moneyReceipt?.pdf_content?.vehicle_info?.price ??
            moneyReceipt?.car_price ??
            "",
          registration_cost:
            moneyReceipt?.pdf_content?.vehicle_info?.registration_cost ??
            moneyReceipt?.registration_cost ??
            "",
          discount:
            moneyReceipt?.pdf_content?.vehicle_info?.discount ??
            moneyReceipt?.vehicle?.discount_price ??
            "",
        },
        additional_costs: normalizeAdditionalCosts(
          moneyReceipt?.pdf_content?.additional_costs?.length
            ? moneyReceipt?.pdf_content?.additional_costs
            : [createEmptyAdditionalCost()]
        ),
        complimentary_items: moneyReceipt?.complimentary_items || [],
      },
    }
  }, [moneyReceipt, fromQuotationId, prefillVehicleId])

  const validationSchema = Yup.object({
    customer_id: Yup.mixed().test(
      "customer-id-or-contact",
      "Select a customer or provide customer name.",
      function (value) {
        return Boolean(value) || hasCustomerIdentifier(this.parent)
      }
    ),
    vehicle_id: Yup.mixed().test(
      "vehicle-id-or-identity",
      "Select a vehicle or provide full chassis/full engine.",
      function (value) {
        return Boolean(value) || hasVehicleIdentifier(this.parent)
      }
    ),
    car_price: Yup.number().min(0).required("Car price is required"),
    registration_cost: Yup.number().min(0).nullable(),
    pdf_content: Yup.object({
      customer_info: Yup.object({
        customer_type: Yup.string().test(
          "customer-type-required",
          "Customer type is required for onboarding.",
          function (value) {
            if (this.from?.[1]?.value?.customer_id) return true
            if (!hasCustomerIdentifier(this.from?.[1]?.value)) return true
            return Boolean(String(value || "").trim())
          }
        ),
        designation: Yup.string().test(
          "designation-by-type",
          "Designation is required for service and banker customers.",
          function (value) {
            const customerTypeKey = normalizeCustomerTypeValue(
              resolveCustomerTypeKey(this.parent?.customer_type)
            )
            if (!["service", "banker"].includes(customerTypeKey)) return true
            return Boolean(String(value || "").trim())
          }
        ),
        company_name: Yup.string().test(
          "company-by-type",
          "Company name is required for service customers.",
          function (value) {
            return resolveCustomerTypeKey(this.parent?.customer_type) !==
              "service"
              ? true
              : Boolean(String(value || "").trim())
          }
        ),
        bank_name: Yup.string().test(
          "bank-by-type",
          "Bank name is required for banker customers.",
          function (value) {
            return resolveCustomerTypeKey(this.parent?.customer_type) !==
              "banker"
              ? true
              : Boolean(String(value || "").trim())
          }
        ),
        branch_name: Yup.string().test(
          "branch-by-type",
          "Branch name is required for banker customers.",
          function (value) {
            return resolveCustomerTypeKey(this.parent?.customer_type) !==
              "banker"
              ? true
              : Boolean(String(value || "").trim())
          }
        ),
      }),
      additional_costs: Yup.array().of(
        Yup.object({
          name: Yup.string().nullable(),
          amount: Yup.number().min(0, "Amount must be 0 or more").nullable(),
        })
      ),
    }),
    issued_date: Yup.string().required("Issued date is required"),
    edit_reason:
      isEdit && amountPaid > 0
        ? Yup.string()
            .trim()
            .required("Edit reason is required after payment has been received.")
        : Yup.string().nullable(),
    quotation_id:
      mode === "quotation"
        ? Yup.number().required("Quotation is required")
        : Yup.mixed().nullable(),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async values => {
      try {
        const matchedVehicle = values.vehicle_id
          ? vehicles.find(item => item.id === Number(values.vehicle_id)) || null
          : findMatchingVehicle(values, vehicles)

        const payload = {
          ...values,
          customer_id: values.customer_id || null,
          vehicle_id: values.vehicle_id || matchedVehicle?.id || null,
          quotation_id: values.quotation_id || null,
          registration_cost: values.registration_cost || 0,
          edit_reason: values.edit_reason?.trim() || null,
          pdf_content: {
            ...values.pdf_content,
            additional_costs: (
              values.pdf_content?.additional_costs || []
            ).filter(
              item =>
                String(item?.name || "").trim() ||
                String(item?.amount || "").trim()
            ),
          },
          complimentary_items: (values.pdf_content?.complimentary_items || []).map(
            item => {
              if (item.id) {
                return { id: item.id, custom_name: item.custom_name || null }
              }
              return { name: item.name }
            }
          ),
        }

        const response = isEdit
          ? await editMoneyReceipt({ id, ...payload })
          : await createMoneyReceipt(payload)

        handleResponse(
          response,
          `Money receipt ${isEdit ? "updated" : "created"} successfully.`,
          true
        )

        if (response?.overpaid_warning) {
          toast.warning(
            `Receipt is now overpaid by ${formatAmount(
              response.overpaid_amount
            )}. Add a refund from the Payments section.`
          )
        }

        const nextId = response?.data?.id ?? id
        if (isEdit) {
          const target =
            fromPath || (nextId ? `/money-receipts/${nextId}` : "/money-receipts")
          navigate(appendQueryParam(target, "mr_focus", nextId))
        } else {
          navigate(nextId ? `/money-receipts/${nextId}` : "/money-receipts")
        }
      } catch (error) {
        handleError(error, "Failed to save money receipt.")
      }
    },
  })

  const selectedComplimentaryNames = (
    formik.values.pdf_content?.complimentary_items || []
  )
    .map(item => item?.name)
    .filter(Boolean)
    .map(name => String(name).trim().toLowerCase())

  const selectedAdditionalCostNames = (
    formik.values.pdf_content?.additional_costs || []
  )
    .map(item => item?.name)
    .filter(Boolean)
    .map(name => String(name).trim().toLowerCase())

  const costNameOptions = useMemo(
    () =>
      (availableComplimentaryItems || [])
        .filter(
          item =>
            !selectedComplimentaryNames.includes(
              String(item?.name || "").trim().toLowerCase()
            ) &&
            !selectedAdditionalCostNames.includes(
              String(item?.name || "").trim().toLowerCase()
            )
        )
        .map(item => ({
          value: item.name,
          label: item.name,
        })),
    [
      availableComplimentaryItems,
      selectedComplimentaryNames,
      selectedAdditionalCostNames,
    ]
  )

  const selectedQuotationOption = useMemo(
    () =>
      quotationOptions.find(
        option => option.value === Number(formik.values.quotation_id)
      ) || null,
    [quotationOptions, formik.values.quotation_id]
  )

  const applyQuotation = option => {
    const quotation = option?.item
    const { carPrice, registrationCost } = getQuotationPricing(quotation)
    formik.setFieldValue("quotation_id", quotation?.id || "")
    formik.setFieldValue("customer_id", quotation?.customer_id || "")
    formik.setFieldValue("vehicle_id", quotation?.vehicle_id || "")
    setSelectedCustomerItem(quotation?.customer ?? null)
    setSelectedVehicleItem(quotation?.vehicle ?? null)

    formik.setFieldValue("car_price", carPrice)
    formik.setFieldValue("registration_cost", registrationCost)
    formik.setFieldValue(
      "pdf_content.customer_info",
      buildCustomerPdfContent(quotation?.customer)
    )
    formik.setFieldValue(
      "pdf_content.vehicle_info",
      buildVehiclePdfContent(
        quotation?.vehicle,
        quotation,
        formik.values.pdf_content?.vehicle_info,
        { carPrice, registrationCost }
      )
    )
    formik.setFieldValue("pdf_content.additional_costs", [
      createEmptyAdditionalCost(),
    ])
    lastAutofilledQuotationIdRef.current = quotation?.id || null
  }

  const refillCustomerPdfContent = customer =>
    formik.setFieldValue(
      "pdf_content.customer_info",
      buildCustomerPdfContent(customer)
    )

  const handleCustomerTypeChange = value => {
    const nextTypeKey = resolveCustomerTypeKey(value)

    formik.setFieldValue("pdf_content.customer_info.customer_type", value)

    if (nextTypeKey === "general") {
      formik.setFieldValue("pdf_content.customer_info.designation", "")
      formik.setFieldValue("pdf_content.customer_info.company_name", "")
      formik.setFieldValue("pdf_content.customer_info.bank_name", "")
      formik.setFieldValue("pdf_content.customer_info.branch_name", "")
      return
    }

    if (nextTypeKey === "service") {
      formik.setFieldValue("pdf_content.customer_info.bank_name", "")
      formik.setFieldValue("pdf_content.customer_info.branch_name", "")
      return
    }

    formik.setFieldValue("pdf_content.customer_info.company_name", "")
  }

  const clearCustomerSelection = () => {
    formik.setFieldValue("customer_id", "")
    setSelectedCustomerItem(null)
    refillCustomerPdfContent(null)
  }

  const handleCustomerSelect = customer => {
    setSelectedCustomerItem(customer)
    formik.setFieldValue("customer_id", customer?.id || "")
    refillCustomerPdfContent(customer)
  }

  const refillVehiclePdfContent = vehicle =>
    formik.setFieldValue(
      "pdf_content.vehicle_info",
      buildVehiclePdfContent(
        vehicle,
        selectedQuotationOption?.item,
        formik.values.pdf_content?.vehicle_info,
        {
          carPrice: formik.values.car_price,
          registrationCost: formik.values.registration_cost,
        }
      )
    )

  const clearVehicleSelection = () => {
    formik.setFieldValue("vehicle_id", "")
    setSelectedVehicleItem(null)
    refillVehiclePdfContent(null)
  }

  const handleVehicleSelect = vehicle => {
    setSelectedVehicleItem(vehicle)
    formik.setFieldValue("vehicle_id", vehicle?.id || "")
    refillVehiclePdfContent(vehicle)
    setVehicleSearchTerm("")
    setShowVehicleSuggestions(false)
  }

  const handleAddComplimentaryItem = item => {
    const currentItems = formik.values.pdf_content?.complimentary_items || []
    formik.setFieldValue("pdf_content.complimentary_items", [...currentItems, item])
  }

  const handleEditComplimentaryItem = (item, newName) => {
    const currentItems = formik.values.pdf_content?.complimentary_items || []
    formik.setFieldValue(
      "pdf_content.complimentary_items",
      currentItems.map(i =>
        i.id === item.id || i.name === item.name
          ? { ...i, custom_name: newName }
          : i
      )
    )
  }

  const handleDeleteComplimentaryItem = item => {
    const currentItems = formik.values.pdf_content?.complimentary_items || []
    formik.setFieldValue(
      "pdf_content.complimentary_items",
      currentItems.filter(i => i.id !== item.id && i.name !== item.name)
    )
  }

  useEffect(() => {
    if (isEdit || mode !== "quotation") return

    const selectedQuotation = selectedQuotationOption?.item
    const selectedQuotationId = selectedQuotation?.id || null

    if (!selectedQuotationId) {
      lastAutofilledQuotationIdRef.current = null
      return
    }

    if (lastAutofilledQuotationIdRef.current === selectedQuotationId) return

    formik.setFieldValue("customer_id", selectedQuotation.customer_id || "")
    formik.setFieldValue("vehicle_id", selectedQuotation.vehicle_id || "")
    setSelectedCustomerItem(selectedQuotation?.customer ?? null)
    setSelectedVehicleItem(selectedQuotation?.vehicle ?? null)

    const { carPrice, registrationCost } =
      getQuotationPricing(selectedQuotation)
    formik.setFieldValue("car_price", carPrice)
    formik.setFieldValue("registration_cost", registrationCost)
    formik.setFieldValue(
      "pdf_content.customer_info",
      buildCustomerPdfContent(selectedQuotation?.customer)
    )
    formik.setFieldValue(
      "pdf_content.vehicle_info",
      buildVehiclePdfContent(
        selectedQuotation?.vehicle,
        selectedQuotation,
        formik.values.pdf_content?.vehicle_info,
        { carPrice, registrationCost }
      )
    )
    formik.setFieldValue("pdf_content.additional_costs", [
      createEmptyAdditionalCost(),
    ])
    lastAutofilledQuotationIdRef.current = selectedQuotationId
  }, [formik, isEdit, mode, selectedQuotationOption])

  useEffect(() => {
    formik.setFieldValue(
      "pdf_content.vehicle_info.price",
      formik.values.car_price || ""
    )
    formik.setFieldValue(
      "pdf_content.vehicle_info.registration_cost",
      formik.values.registration_cost || 0
    )
  }, [formik.values.car_price, formik.values.registration_cost])

  const selectedCustomerType = normalizeCustomerTypeValue(
    resolveCustomerTypeKey(
      formik.values.pdf_content?.customer_info?.customer_type ||
        selectedCustomerItem?.customer_type
    )
  )
  const selectedCustomerTypeMeta =
    getCustomerTypeFieldMeta(selectedCustomerType)
  const isServiceCustomer = selectedCustomerType === "service"
  const isBankerCustomer = selectedCustomerType === "banker"
  const selectedCustomerProfileRows = buildCustomerPreviewRows(
    selectedCustomerItem
  )

  useEffect(() => {
    if (isEdit) return
    if (cuidPrefilledRef.current) return
    const customer = prefillCustomerResponse?.data
    if (!customer?.id) return

    setSelectedCustomerItem(customer)
    formik.setFieldValue("customer_id", customer.id)
    refillCustomerPdfContent(customer)
    cuidPrefilledRef.current = true
  }, [isEdit, prefillCustomerResponse?.data])

  useEffect(() => {
    if (!isEdit || !moneyReceipt?.id) return
    if (lastEditPrefilledReceiptIdRef.current === moneyReceipt.id) return

    setSelectedVehicleItem(moneyReceipt?.vehicle ?? null)
    setSelectedCustomerItem(moneyReceipt?.customer ?? null)

    if (moneyReceipt?.customer) {
      formik.setFieldValue(
        "pdf_content.customer_info",
        buildCustomerPdfContent({
          ...moneyReceipt.customer,
          name:
            formik.values.pdf_content?.customer_info?.name ||
            moneyReceipt.customer?.name ||
            "",
          mobile:
            formik.values.pdf_content?.customer_info?.mobile ||
            moneyReceipt.customer?.mobile ||
            "",
          email:
            formik.values.pdf_content?.customer_info?.email ||
            moneyReceipt.customer?.email ||
            "",
          full_address:
            formik.values.pdf_content?.customer_info?.address ||
            moneyReceipt.customer?.full_address ||
            "",
        })
      )
    }

    if (moneyReceipt?.vehicle) {
      formik.setFieldValue(
        "pdf_content.vehicle_info",
        buildVehiclePdfContent(
          moneyReceipt.vehicle,
          selectedQuotationOption?.item,
          formik.values.pdf_content?.vehicle_info,
          {
            carPrice: formik.values.car_price,
            registrationCost: formik.values.registration_cost,
          }
        )
      )
    }

    lastEditPrefilledReceiptIdRef.current = moneyReceipt.id
  }, [
    formik,
    isEdit,
    moneyReceipt?.id,
    moneyReceipt?.vehicle,
    moneyReceipt?.customer,
    selectedQuotationOption,
  ])

  useEffect(() => {
    if (isEdit || vehiclePrefilledRef.current) return
    if (!prefillVehicleId || !prefillVehicleResponse?.data) return
    setSelectedVehicleItem(prefillVehicleResponse.data)
    refillVehiclePdfContent(prefillVehicleResponse.data)
    vehiclePrefilledRef.current = true
  }, [isEdit, prefillVehicleId, prefillVehicleResponse?.data])

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        vehicleSearchRef.current &&
        !vehicleSearchRef.current.contains(event.target)
      ) {
        setShowVehicleSuggestions(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const additionalCosts = formik.values.pdf_content?.additional_costs || []
  const additionalCostsTotal = getAdditionalCostsTotal(additionalCosts)
  const totalAmount =
    Number(formik.values.car_price || 0) +
    Number(formik.values.registration_cost || 0) +
    additionalCostsTotal
  const overpaidAmount = Math.max(0, amountPaid - totalAmount)
  const dueAmount = Math.max(0, totalAmount - amountPaid)
  const summaryInfo = [
    { label: "Car Price", value: Number(formik.values.car_price || 0) },
    {
      label: "Registration Cost",
      value: Number(formik.values.registration_cost || 0),
    },
    ...additionalCosts.map((item, index) => ({
      label: item?.name || `Additional Cost ${index + 1}`,
      value: Number(item?.amount || 0),
    })),
    { label: "Total Amount", value: totalAmount, strong: true },
    { label: "Amount Paid", value: amountPaid },
    { label: "Overpaid", value: overpaidAmount },
    { label: "Due", value: dueAmount, strong: dueAmount > 0 },
  ].filter(item => item.value > 0 || item.label === "Total Amount")

  return (
    <PageWrapper
      title="Money Receipt"
      breadcrumbItem={isEdit ? "Edit Money Receipt" : "Create Money Receipt"}
    >
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <div>
          <h4 className="mb-0">
            {isEdit ? "Edit Money Receipt" : "Create Money Receipt"}
          </h4>
          <div className="text-muted">
            Use quotation reference or create manually.
          </div>
        </div>
        <Button color="light" onClick={() => navigate(fromPath)}>
          Back
        </Button>
      </div>

      <Card className="border-0 shadow-none mb-0">
        <CardBody className="px-0 px-sm-2 px-md-4">
          {isPaidReceipt && (
            <Alert color="warning">
              This receipt already has recorded payments. Updating customer, vehicle, price, registration cost,
              additional cost, or discount will recalculate the due and overpaid amounts from the existing payment
              history. Refunds must still be handled as separate payment/refund actions.
            </Alert>
          )}

          {!isEdit && (
            <div className="mb-4">
              <Label className="form-label d-block">Creation Mode</Label>
              <div className="d-flex gap-3">
                <Button
                  color={mode === "manual" ? "primary" : "light"}
                  onClick={() => setMode("manual")}
                >
                  Manual
                </Button>
                <Button
                  color={mode === "quotation" ? "primary" : "light"}
                  onClick={() => setMode("quotation")}
                >
                  From Quotation
                </Button>
              </div>
            </div>
          )}

          {mode === "quotation" && !isEdit && (
            <Alert color="info">
              Selecting a quotation will auto-fill customer, vehicle, pricing,
              and printable receipt fields. Everything remains editable before
              saving.
            </Alert>
          )}

          {isLoading ? (
            <div>Loading...</div>
          ) : (
            <Form onSubmit={formik.handleSubmit}>
              <div className="a4-container">
                <div className="a4-paper">
                  <div className="mr-header">
                    <Row className="align-items-start g-3">
                      <Col md={7}>
                        <div className="mr-brand">AUROBIL CARS</div>
                        <div className="text-muted small">
                          Money receipt entry form
                        </div>
                        <div className="small mt-2">
                          The saved receipt will follow the same structure as
                          the printable PDF.
                        </div>
                      </Col>
                      <Col md={5}>
                        <div className="receipt-info">
                          <div className="d-flex justify-content-between">
                            <span className="fw-semibold">MR No.</span>
                            <span>
                              {moneyReceipt?.mr_no || "Auto-generated"}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between mt-2">
                            <span className="fw-semibold">Date</span>
                            <span>{formik.values.issued_date || "-"}</span>
                          </div>
                          {(mode === "quotation" || isEdit) && (
                            <div className="d-flex justify-content-between mt-2">
                              <span className="fw-semibold">Quotation</span>
                              <span>
                                {selectedQuotationOption?.item?.qid || "-"}
                              </span>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>

                  <Row className="g-3">
                    {(mode === "quotation" || isEdit) && (
                      <Col md={12}>
                        <Label className="form-label">
                          Quotation
                          {mode === "quotation" && (
                            <span className="text-danger ms-1">*</span>
                          )}
                        </Label>
                        <Select
                          options={quotationOptions}
                          value={selectedQuotationOption}
                          onChange={option => applyQuotation(option)}
                          onBlur={() =>
                            formik.setFieldTouched("quotation_id", true)
                          }
                          isClearable={true}
                        />
                        {formik.touched.quotation_id &&
                          formik.errors.quotation_id && (
                            <div className="text-danger small mt-1">
                              {formik.errors.quotation_id}
                            </div>
                          )}
                      </Col>
                    )}

                    <Col md={4}>
                      <Label className="form-label">Money Receipt No.</Label>
                      <Input
                        value={moneyReceipt?.mr_no || "Auto-generated"}
                        disabled
                      />
                    </Col>

                    <Col md={4}>
                      <Label className="form-label">Issued Date</Label>
                      <Input
                        type="date"
                        name="issued_date"
                        value={formik.values.issued_date}
                        onChange={formik.handleChange}
                        invalid={
                          !!(
                            formik.touched.issued_date &&
                            formik.errors.issued_date
                          )
                        }
                      />
                      <FormFeedback>{formik.errors.issued_date}</FormFeedback>
                    </Col>

                    {(mode === "quotation" || isEdit) && (
                      <Col md={4}>
                        <Label className="form-label">Quotation No.</Label>
                        <Input
                          name="pdf_content.vehicle_info.quotation"
                          value={
                            formik.values.pdf_content?.vehicle_info
                              ?.quotation || ""
                          }
                          readOnly
                          disabled
                          placeholder="Quotation reference shown in PDF"
                        />
                      </Col>
                    )}

                    <Col md={12}>
                      <div className="section-header">
                        <h6>CUSTOMER INFORMATION</h6>
                        <hr />
                      </div>
                    </Col>

                    <Col md={12}>
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                        <Label className="form-label mb-0">Customer</Label>
                        {selectedCustomerItem && (
                          <div className="d-flex gap-2 flex-wrap">
                            {!customerLocked && (
                              <Button
                                color="outline-danger"
                                size="sm"
                                type="button"
                                onClick={clearCustomerSelection}
                              >
                                <i className="bx bx-x me-1"></i>
                                Clear
                              </Button>
                            )}
                            <Button
                              color="outline-success"
                              size="sm"
                              type="button"
                              onClick={() =>
                                refillCustomerPdfContent(selectedCustomerItem)
                              }
                            >
                              <i className="bx bx-refresh me-1"></i>
                              Refill
                            </Button>
                          </div>
                        )}
                      </div>

                      {!selectedCustomerItem && customerLocked && (
                        <div className="text-muted small">
                          {prefillCustomerLoading
                            ? "Loading customer..."
                            : "Customer not found."}
                        </div>
                      )}

                      {!selectedCustomerItem && !customerLocked && (
                        <CustomerSearch
                          onCustomerSelect={handleCustomerSelect}
                          apiSearchFunction={customerSearchAPI}
                          placeholder="Search customers by name, email, phone..."
                          isSearching={customerSearching}
                        />
                      )}

                      {formik.touched.customer_id &&
                        formik.errors.customer_id && (
                          <div className="text-danger small mt-1">
                            {formik.errors.customer_id}
                          </div>
                        )}
                    </Col>

                    {selectedCustomerItem && (
                      <Col md={12}>
                        <div className="selection-preview-card customer-preview-card">
                          <div className="selection-preview-icon">
                            <i className="bx bx-user"></i>
                          </div>
                          <div className="w-100">
                            <h6 className="mb-1">Customer Details</h6>
                            <div className="text-muted small mb-2">
                              ID:{" "}
                              {selectedCustomerItem?.cuid ||
                                selectedCustomerItem?.id ||
                                "-"}
                              {"  "}Name: {selectedCustomerItem?.name || "-"}
                            </div>
                            <Row className="g-2 small">
                              {selectedCustomerProfileRows.map(row => (
                                <Col xs={12} sm={6} key={row.label}>
                                  <span className="text-muted">
                                    {row.label}:
                                  </span>{" "}
                                  {row.value}
                                </Col>
                              ))}
                            </Row>
                          </div>
                        </div>
                      </Col>
                    )}

                    <Col md={12}>
                      <div className="editor-panel">
                        <Row className="g-3">
                          <Col xs={12} sm={6}>
                            <Label className="form-label">Name</Label>
                            <Input
                              name="pdf_content.customer_info.name"
                              value={
                                formik.values.pdf_content?.customer_info
                                  ?.name || ""
                              }
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col xs={12} sm={6} md={6}>
                            <Label className="form-label">Mobile</Label>
                            <Input
                              name="pdf_content.customer_info.mobile"
                              value={
                                formik.values.pdf_content?.customer_info
                                  ?.mobile || ""
                              }
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col xs={12} sm={6} md={6}>
                            <Label className="form-label">Email</Label>
                            <Input
                              name="pdf_content.customer_info.email"
                              value={
                                formik.values.pdf_content?.customer_info
                                  ?.email || ""
                              }
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col xs={12} sm={6} lg={6}>
                            <FormikCreatableSelect
                              name="pdf_content.customer_info.customer_type"
                              label="Customer Type"
                              formik={formik}
                              options={customerTypeSelectOptions}
                              placeholder="Select or type customer type"
                              loading={customerLookupLoading}
                              required
                              onChange={handleCustomerTypeChange}
                            />
                          </Col>
                          {(isServiceCustomer || isBankerCustomer) && (
                            <Col xs={12} sm={6} lg={4}>
                              <FormikCreatableSelect
                                name="pdf_content.customer_info.designation"
                                label="Designation"
                                formik={formik}
                                options={designationSelectOptions}
                                placeholder="Select or type a designation"
                                loading={customerLookupLoading}
                                required
                              />
                            </Col>
                          )}
                          {(isServiceCustomer || isBankerCustomer) && (
                            <Col xs={12} sm={6} lg={4}>
                              <FormikCreatableSelect
                                name={
                                  isBankerCustomer
                                    ? "pdf_content.customer_info.bank_name"
                                    : "pdf_content.customer_info.company_name"
                                }
                                label={
                                  selectedCustomerTypeMeta.organizationLabel
                                }
                                formik={formik}
                                options={
                                  isBankerCustomer
                                    ? bankSelectOptions
                                    : companySelectOptions
                                }
                                placeholder={`Select or type ${selectedCustomerTypeMeta.organizationLabel.toLowerCase()}`}
                                loading={customerLookupLoading}
                                required
                              />
                            </Col>
                          )}
                          {isBankerCustomer && (
                            <>
                              <Col xs={12} sm={6} lg={4}>
                                <FormikCreatableSelect
                                  name="pdf_content.customer_info.branch_name"
                                  label="Branch Name"
                                  formik={formik}
                                  options={branchSelectOptions}
                                  placeholder="Select or type branch name"
                                  loading={customerLookupLoading}
                                  required
                                />
                              </Col>
                            </>
                          )}
                          <Col md={12}>
                            <Label className="form-label">Address</Label>
                            <Input
                              type="textarea"
                              rows={4}
                              name="pdf_content.customer_info.address"
                              value={
                                formik.values.pdf_content?.customer_info
                                  ?.address || ""
                              }
                              onChange={formik.handleChange}
                              placeholder="Customer address shown in the PDF"
                            />
                          </Col>
                        </Row>
                      </div>
                    </Col>

                    <Col md={12}>
                      <div className="section-header">
                        <h6>VEHICLE INFORMATION</h6>
                        <hr />
                      </div>
                    </Col>

                    <Col md={12}>
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                        <Label className="form-label mb-0">Vehicle</Label>
                        {selectedVehicleItem && (
                          <div className="d-flex gap-2 flex-wrap">
                            {!vehicleLocked && (
                              <Button
                                color="outline-danger"
                                size="sm"
                                type="button"
                                onClick={clearVehicleSelection}
                              >
                                <i className="bx bx-x me-1"></i>
                                Clear
                              </Button>
                            )}
                            <Button
                              color="outline-success"
                              size="sm"
                              type="button"
                              onClick={() =>
                                refillVehiclePdfContent(selectedVehicleItem)
                              }
                            >
                              <i className="bx bx-refresh me-1"></i>
                              Refill
                            </Button>
                          </div>
                        )}
                      </div>

                      {!selectedVehicleItem && vehicleLocked && (
                        <div className="text-muted small">
                          {prefillVehicleLoading
                            ? "Loading vehicle..."
                            : "Vehicle not found."}
                        </div>
                      )}

                      {!selectedVehicleItem && !vehicleLocked && (
                        <div
                          className="vehicle-selector position-relative"
                          ref={vehicleSearchRef}
                        >
                          <Input
                            type="text"
                            placeholder="Search by vehicle name, chassis no, stock id, or engine no..."
                            value={vehicleSearchTerm}
                            onChange={e => {
                              setVehicleSearchTerm(e.target.value)
                              setShowVehicleSuggestions(true)
                            }}
                            onFocus={() => setShowVehicleSuggestions(true)}
                          />
                          {vehicleSearching && (
                            <small className="text-muted d-flex align-items-center gap-1 mt-1">
                              <Spinner size="sm" type="grow" />
                              Searching vehicles...
                            </small>
                          )}

                          {showVehicleSuggestions &&
                            vehicleSearchResults?.length > 0 && (
                              <Card
                                className="position-absolute w-100 mt-1 shadow"
                                style={{ zIndex: 1000 }}
                              >
                                <CardBody className="p-2">
                                  {vehicleSearchResults.map(vehicle => (
                                    <div
                                      key={vehicle.id}
                                      className="p-2 border-bottom cursor-pointer hover-bg"
                                      onClick={() =>
                                        handleVehicleSelect(vehicle)
                                      }
                                    >
                                      <div className="fw-bold text-primary">
                                        {getVehiclePreviewLabel(vehicle) ||
                                          "Unknown Vehicle"}
                                      </div>
                                      <div className="small text-muted">
                                        Stock: {vehicle?.stock_id || "-"}
                                        {" | "}Chassis:{" "}
                                        {vehicle?.full_chassis || "-"}
                                        {" | "}Engine:{" "}
                                        {vehicle?.full_engine || "-"}
                                        {" | "}Color:{" "}
                                        {getDisplayText(
                                          vehicle?.exterior_color
                                        ) || "-"}
                                      </div>
                                    </div>
                                  ))}
                                </CardBody>
                              </Card>
                            )}

                          {showVehicleSuggestions &&
                            vehicleSearchTerm.length >= 2 &&
                            !vehicleSearching &&
                            !vehicleSearchResults?.length && (
                              <Card className="position-absolute w-100 mt-1">
                                <CardBody className="p-3 text-center text-muted">
                                  <i className="bx bx-car fs-4 mb-2 d-block"></i>
                                  No vehicles found
                                  <div className="small mt-1">
                                    Try different search terms
                                  </div>
                                </CardBody>
                              </Card>
                            )}
                        </div>
                      )}

                      {formik.touched.vehicle_id &&
                        formik.errors.vehicle_id && (
                          <div className="text-danger small mt-1">
                            {formik.errors.vehicle_id}
                          </div>
                        )}
                    </Col>

                    {selectedVehicleItem && (
                      <Col md={12}>
                        <div className="selection-preview-card vehicle-preview-card">
                          <div className="selection-preview-icon">
                            <i className="bx bx-car"></i>
                          </div>
                          <div className="w-100">
                            <h6 className="mb-1">Vehicle Details</h6>
                            <div className="text-muted small">
                              Stock: {selectedVehicleItem?.stock_id || "-"}
                              {"  "}Chassis:{" "}
                              {selectedVehicleItem?.full_chassis || "-"}
                            </div>
                            {getVehiclePreviewLabel(selectedVehicleItem) && (
                              <div className="small mt-1">
                                {getVehiclePreviewLabel(selectedVehicleItem)}
                              </div>
                            )}
                            <div className="small mt-2">
                              Color:{" "}
                              {getDisplayText(
                                selectedVehicleItem?.exterior_color
                              ) || "-"}
                              {"  "}Year:{" "}
                              {getDisplayText(selectedVehicleItem?.year) ||
                                "-"}
                              {"  "}Mileage:{" "}
                              {selectedVehicleItem?.mileage || "-"}
                            </div>
                            <div className="small mt-1">
                              Engine Type:{" "}
                              {getDisplayText(
                                selectedVehicleItem?.engine_type
                              ) || "-"}
                              {"  "}Fuel:{" "}
                              {getDisplayText(
                                selectedVehicleItem?.fuel_type
                              ) || "-"}
                              {"  "}CC:{" "}
                              {getDisplayText(
                                selectedVehicleItem?.engine_c_c
                              ) || "-"}
                            </div>
                            <div className="small mt-1">
                              BRTA Reg:{" "}
                              {selectedVehicleItem?.brta_reg_no || "-"}
                              {"  "}Engine:{" "}
                              {selectedVehicleItem?.full_engine || "-"}
                            </div>
                          </div>
                        </div>
                      </Col>
                    )}

                    <Col md={12}>
                      <div className="editor-panel">
                        <Row className="g-3">
                          <Col md={12}>
                            <Label className="form-label">Name</Label>
                            <Input
                              name="pdf_content.vehicle_info.name"
                              value={
                                formik.values.pdf_content?.vehicle_info?.name ||
                                ""
                              }
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col md={6}>
                            <Label className="form-label">Full Chassis</Label>
                            <Input
                              name="pdf_content.vehicle_info.full_chassis"
                              value={
                                formik.values.pdf_content?.vehicle_info
                                  ?.full_chassis || ""
                              }
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col md={6}>
                            <Label className="form-label">Full Engine</Label>
                            <Input
                              name="pdf_content.vehicle_info.full_engine"
                              value={
                                formik.values.pdf_content?.vehicle_info
                                  ?.full_engine || ""
                              }
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col md={4}>
                            <Label className="form-label">Year/Model</Label>
                            <Input
                              name="pdf_content.vehicle_info.year_model"
                              value={
                                formik.values.pdf_content?.vehicle_info
                                  ?.year_model || ""
                              }
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col md={4}>
                            <Label className="form-label">Color</Label>
                            <Input
                              name="pdf_content.vehicle_info.color"
                              value={
                                formik.values.pdf_content?.vehicle_info
                                  ?.color || ""
                              }
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col md={4}>
                            <Label className="form-label">CC</Label>
                            <Input
                              name="pdf_content.vehicle_info.cc"
                              value={
                                formik.values.pdf_content?.vehicle_info?.cc ||
                                ""
                              }
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col md={6}>
                            <Label className="form-label">Fuel</Label>
                            <Input
                              name="pdf_content.vehicle_info.fuel"
                              value={
                                formik.values.pdf_content?.vehicle_info?.fuel ||
                                ""
                              }
                              onChange={formik.handleChange}
                            />
                          </Col>
                          <Col md={6}>
                            <Label className="form-label">BRTA Reg.</Label>
                            <Input
                              name="pdf_content.vehicle_info.brta_reg"
                              value={
                                formik.values.pdf_content?.vehicle_info
                                  ?.brta_reg || ""
                              }
                              onChange={formik.handleChange}
                            />
                          </Col>
                        </Row>
                      </div>
                    </Col>

                    <Col md={12}>
                      <div className="section-header">
                        <h6>PAYMENT SUMMARY</h6>
                        <hr />
                      </div>
                    </Col>

                    <Col md={4}>
                      <Label className="form-label">Price</Label>
                      <Input
                        type="number"
                        name="car_price"
                        value={formik.values.car_price}
                        onChange={formik.handleChange}
                        invalid={
                          !!(
                            formik.touched.car_price && formik.errors.car_price
                          )
                        }
                      />
                      <FormFeedback>{formik.errors.car_price}</FormFeedback>
                    </Col>

                    <Col md={4}>
                      <Label className="form-label">Registration Cost</Label>
                      <Input
                        type="number"
                        name="registration_cost"
                        value={formik.values.registration_cost}
                        onChange={formik.handleChange}
                        invalid={
                          !!(
                            formik.touched.registration_cost &&
                            formik.errors.registration_cost
                          )
                        }
                      />
                      <FormFeedback>
                        {formik.errors.registration_cost}
                      </FormFeedback>
                    </Col>

                    <Col md={4}>
                      <Label className="form-label">Discount</Label>
                      <Input
                        type="number"
                        name="pdf_content.vehicle_info.discount"
                        value={
                          formik.values.pdf_content?.vehicle_info?.discount ??
                          ""
                        }
                        onChange={formik.handleChange}
                      />
                    </Col>

                    <Col md={12}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Label className="form-label mb-0">
                          Additional Costs
                        </Label>
                        <Button
                          type="button"
                          color="outline-primary"
                          size="sm"
                          onClick={() =>
                            formik.setFieldValue(
                              "pdf_content.additional_costs",
                              [...additionalCosts, createEmptyAdditionalCost()]
                            )
                          }
                        >
                          <i className="bx bx-plus me-1"></i>
                          Add Cost
                        </Button>
                      </div>
                      <Row className="g-2">
                        {additionalCosts.map((item, index) => (
                          <Col md={12} key={`additional-cost-${index}`}>
                            <Row className="g-2 align-items-start">
                              <Col xs={12} md={7}>
                                <FormikCreatableSelect
                                  name={`pdf_content.additional_costs.${index}.name`}
                                  formik={formik}
                                  options={costNameOptions}
                                  placeholder="Cost name"
                                />
                              </Col>
                              <Col xs={10} md={4}>
                                <Input
                                  type="number"
                                  name={`pdf_content.additional_costs.${index}.amount`}
                                  value={item?.amount ?? ""}
                                  onChange={formik.handleChange}
                                  placeholder="Amount"
                                />
                              </Col>
                              <Col xs={2} md={1}>
                                <Button
                                  type="button"
                                  color="light"
                                  className="w-100"
                                  onClick={() => {
                                    const nextItems = additionalCosts.filter(
                                      (_, itemIndex) => itemIndex !== index
                                    )
                                    formik.setFieldValue(
                                      "pdf_content.additional_costs",
                                      nextItems.length
                                        ? nextItems
                                        : [createEmptyAdditionalCost()]
                                    )
                                  }}
                                >
                                  <i className="bx bx-trash"></i>
                                </Button>
                              </Col>
                            </Row>
                          </Col>
                        ))}
                      </Row>
                    </Col>

                    <Col md={12}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Label className="form-label mb-0">
                          Complimentary Items
                        </Label>
                      </div>
                      <ComplimentaryItemsManager
                        items={formik.values.pdf_content?.complimentary_items || []}
                        availableItems={availableComplimentaryItems}
                        excludedNames={
                          (formik.values.pdf_content?.additional_costs || [])
                            .map(item => item?.name)
                            .filter(Boolean)
                        }
                        onAdd={handleAddComplimentaryItem}
                        onEdit={handleEditComplimentaryItem}
                        onDelete={handleDeleteComplimentaryItem}
                        canAdd={hasAnyPermission([ACCESS.COMPLIMENTARY_ITEM.CREATE])}
                        canEdit={hasAnyPermission([ACCESS.COMPLIMENTARY_ITEM.EDIT])}
                        canDelete={hasAnyPermission([ACCESS.COMPLIMENTARY_ITEM.DELETE])}
                      />
                    </Col>

                    <Col md={4}>
                      <div className="total-section h-100">
                        {summaryInfo.length > 0 ? (
                          summaryInfo.map(item => (
                            <div
                              key={item.label}
                              className={`d-flex justify-content-between gap-3 ${
                                item.strong
                                  ? "fw-bold fs-6 border-top pt-2 mt-2"
                                  : "mb-2"
                              }`}
                            >
                              <span>{item.label}</span>
                              <span>{formatAmount(item.value)}</span>
                            </div>
                          ))
                        ) : (
                          <div className="text-muted">
                            Enter receipt amounts to build the printable
                            summary.
                          </div>
                        )}
                      </div>
                    </Col>

                    <Col md={12}>
                      <div className="section-header">
                        <h6>NOTES</h6>
                        <hr />
                      </div>
                      <Input
                        type="textarea"
                        name="notes"
                        rows={6}
                        value={formik.values.notes}
                        onChange={formik.handleChange}
                        placeholder="Optional notes for the printed money receipt"
                      />
                    </Col>

                    {isPaidReceipt && (
                      <Col md={12}>
                        <Label className="form-label">Edit Reason</Label>
                        <Input
                          type="textarea"
                          name="edit_reason"
                          rows={3}
                          value={formik.values.edit_reason}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={!!(formik.touched.edit_reason && formik.errors.edit_reason)}
                          placeholder="Why is this paid receipt being updated?"
                        />
                        <FormFeedback>{formik.errors.edit_reason}</FormFeedback>
                      </Col>
                    )}

                    <Col md={12}>
                      <div className="d-flex gap-2 justify-content-sm-end flex-column flex-sm-row">
                        <Button
                          type="submit"
                          color="primary"
                          disabled={creating || editing}
                        >
                          {isEdit ? "Update" : "Create"} Money Receipt
                        </Button>
                        <Button
                          color="light"
                          onClick={() => navigate(fromPath)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </Form>
          )}
        </CardBody>
      </Card>
    </PageWrapper>
  )
}

export default MoneyReceiptAddEditPage
